// Client-side auth-token sanity checks.
//
// The backend is the real authority on auth (it verifies the JWT signature), but
// the frontend can cheaply reject a token that is obviously unusable *before*
// rendering a protected page — so a stale/junk token doesn't slip past
// ProtectedRoute and trigger a "no email" error from the API.
//
// "Unusable" here means: missing, the literal strings "null"/"undefined" (the
// result of stringifying a null token into localStorage), not a well-formed JWT,
// or past its expiry. A token with a bad signature still looks valid here — only
// the backend can catch that, which it does with a 401 (see getPlayerChips).

// Decode a JWT's payload segment without verifying the signature. Returns the
// claims object, or null if the token isn't a structurally valid JWT.
function decodeJwtPayload(token) {
    try {
        const part = token.split(".")[1];
        if (!part) return null;
        // base64url -> base64, then pad to a multiple of 4 for atob.
        let b64 = part.replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        const json = decodeURIComponent(
            atob(b64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
}

// True only if `token` is a well-formed JWT that hasn't expired. Anything else
// (missing, "null"/"undefined", malformed, or expired) counts as logged-out.
export function isTokenValid(token) {
    if (!token || token === "null" || token === "undefined") return false;
    const payload = decodeJwtPayload(token);
    if (!payload) return false;
    // exp is seconds since epoch; treat a past (or present) exp as expired.
    if (typeof payload.exp === "number" && Date.now() >= payload.exp * 1000) {
        return false;
    }
    return true;
}

// Read the persisted token, returning it only if it's valid. A junk/expired token
// is purged from localStorage so it can't keep firing rejected API calls.
export function getValidToken() {
    const token = localStorage.getItem("token");
    if (isTokenValid(token)) return token;
    if (token !== null) localStorage.removeItem("token");
    return null;
}
